import { defineField, defineType } from 'sanity'
import AIRulesOverview from '../../components/AIRulesOverview'
import PromptTemplateInput from '../../components/PromptTemplateInput'

export default defineType({
  name: 'aiGenerationRules',
  type: 'document',
  title: 'AI Generation Rules',
  groups: [
    {
      name: 'overview',
      title: 'Overview',
      default: true,
    },
    {
      name: 'global',
      title: 'Global Settings',
    },
    {
      name: 'buyerGuides',
      title: 'Buyer Guide Rules',
    },
  ],
  preview: {
    select: {
      name: 'name',
      version: 'version',
      isActive: 'isActive',
    },
    prepare({ name, version, isActive }) {
      return {
        title: name || 'Untitled Rule Set',
        subtitle: `v${version || '1.0.0'}${isActive ? ' • Active' : ''}`,
        media: isActive ? undefined : undefined,
      }
    },
  },
  fields: [
    // ========================================
    // OVERVIEW TAB - Documentation Component
    // ========================================
    defineField({
      name: 'documentation',
      type: 'string',
      group: 'overview',
      components: {
        field: AIRulesOverview,
      },
    }),

    // ========================================
    // GLOBAL SETTINGS
    // ========================================
    defineField({
      name: 'name',
      type: 'string',
      title: 'Rule Set Name',
      description: 'A descriptive name for this rule set (e.g., "Default Rules", "Technical Tone", "Marketing Focus")',
      validation: (Rule) => Rule.required(),
      group: 'global',
    }),
    defineField({
      name: 'isActive',
      type: 'boolean',
      title: 'Active Rule Set',
      description: 'Only one rule set should be active at a time. The active rule set will be used for AI generation.',
      initialValue: false,
      group: 'global',
    }),
    defineField({
      name: 'version',
      type: 'string',
      title: 'Version',
      description:
        'Semantic version (e.g., 1.0.0). Increment when making rule changes.',
      validation: (Rule) => Rule.required(),
      group: 'global',
    }),
    defineField({
      name: 'lastUpdated',
      type: 'datetime',
      title: 'Last Updated',
      readOnly: true,
      group: 'global',
    }),

    // AI Provider Configuration
    defineField({
      name: 'aiProvider',
      type: 'object',
      title: 'AI Provider Configuration',
      group: 'global',
      fields: [
        {
          name: 'provider',
          type: 'string',
          title: 'Provider',
          options: {
            list: [
              {
                title: 'Claude Opus 4.6 (Most Intelligent - Premium)',
                value: 'claude-opus-4-6',
              },
              {
                title: 'Claude Sonnet 4.5 (Latest - Recommended)',
                value: 'claude-sonnet-4-5',
              },
              {
                title: 'Claude Haiku 4.5 (Fast & Economical)',
                value: 'claude-haiku-4-5',
              },
            ],
            layout: 'radio',
          },
          initialValue: 'claude-sonnet-4-5',
          description:
            'Opus 4.6 for highest quality, Sonnet 4.5 for best balance, Haiku 4.5 for cost optimization',
        },
        {
          name: 'temperature',
          type: 'number',
          title: 'Temperature',
          description: 'Controls randomness (0.0 = deterministic, 1.0 = creative)',
          validation: (Rule) => Rule.min(0).max(1).precision(1),
          initialValue: 0.3,
        },
        {
          name: 'maxTokens',
          type: 'number',
          title: 'Max Tokens per Response',
          description: 'Maximum tokens for each AI response',
          initialValue: 1024,
          validation: (Rule) => Rule.min(256).max(4096),
        },
      ],
    }),

    // Company Brand Voice
    defineField({
      name: 'brandVoice',
      type: 'object',
      title: 'Company Brand Voice & Style',
      group: 'global',
      options: {
        collapsible: true,
        collapsed: false,
      },
      fields: [
        {
          name: 'companyName',
          type: 'string',
          title: 'Company Name',
          initialValue: 'Apex Race Parts',
          description: 'Used in AI context',
        },
        {
          name: 'tone',
          type: 'string',
          title: 'Overall Tone',
          options: {
            list: [
              { title: 'Professional & Technical', value: 'professional' },
              { title: 'Enthusiast-Friendly', value: 'enthusiast' },
              { title: 'Expert & Authoritative', value: 'expert' },
              { title: 'Casual & Approachable', value: 'casual' },
            ],
            layout: 'radio',
          },
          initialValue: 'enthusiast',
        },
        {
          name: 'voiceGuidelines',
          type: 'array',
          title: 'Voice Guidelines',
          description:
            'Key principles for writing style (e.g., "Use active voice", "Be specific with measurements")',
          of: [{ type: 'string' }],
          initialValue: [
            'Use active voice and direct language',
            'Be specific with technical details (sizes, specs, measurements)',
            'Avoid marketing fluff and superlatives',
            'Focus on practical fitment information',
            'Acknowledge trade-offs honestly',
          ],
        },
        {
          name: 'terminology',
          type: 'array',
          title: 'Preferred Terminology',
          description: 'Specific terms to use or avoid',
          of: [
            {
              type: 'object',
              fields: [
                {
                  name: 'use',
                  type: 'string',
                  title: 'Use This',
                  placeholder: 'e.g., "fitment"',
                },
                {
                  name: 'instead',
                  type: 'string',
                  title: 'Instead Of',
                  placeholder: 'e.g., "setup"',
                },
              ],
              preview: {
                select: {
                  use: 'use',
                  instead: 'instead',
                },
                prepare({ use, instead }) {
                  return {
                    title: `Use "${use}"`,
                    subtitle: instead ? `Instead of "${instead}"` : '',
                  }
                },
              },
            },
          ],
        },
        {
          name: 'disclaimer',
          type: 'text',
          title: 'Standard Disclaimer',
          description:
            'Optional disclaimer to append to all AI-generated content',
          rows: 3,
          placeholder:
            'e.g., "Always verify fitment with your specific vehicle configuration..."',
        },
      ],
    }),

    // Automation Settings
    defineField({
      name: 'automationSettings',
      type: 'object',
      title: 'Automation Settings',
      group: 'global',
      options: {
        collapsible: true,
        collapsed: false,
      },
      fields: [
        {
          name: 'autoRegenerateOnContextChange',
          type: 'boolean',
          title: 'Auto-regenerate on Context Change',
          description: 'Automatically queue regeneration when fitment data changes',
          initialValue: true,
        },
        {
          name: 'autoRegenerateOnRuleChange',
          type: 'boolean',
          title: 'Auto-regenerate on Rule Change',
          description:
            'Regenerate ALL guides when rules are updated (use cautiously - generates costs)',
          initialValue: false,
        },
        {
          name: 'batchSize',
          type: 'number',
          title: 'Batch Processing Size',
          description:
            'Number of guides to process in parallel during bulk regeneration',
          initialValue: 5,
          validation: (Rule) => Rule.min(1).max(20),
        },
      ],
    }),

    // ========================================
    // BUYER GUIDE SPECIFIC RULES
    // ========================================

    // Overview Rules
    defineField({
      name: 'overviewRules',
      type: 'object',
      title: 'Overview Generation Rules',
      group: 'buyerGuides',
      options: {
        collapsible: true,
        collapsed: false,
      },
      fields: [
        {
          name: 'enabled',
          type: 'boolean',
          title: 'Enable Auto-Generation',
          initialValue: true,
        },
        {
          name: 'promptTemplate',
          type: 'text',
          title: 'Prompt Template',
          description: 'Use {{variable}} syntax for context interpolation. Click "Open Editor" for field picker and live preview.',
          rows: 10,
          components: {
            input: PromptTemplateInput,
          },
        },
        {
          name: 'maxLength',
          type: 'number',
          title: 'Max Length (characters)',
          initialValue: 500,
        },
      ],
    }),

    // Performance Analysis Rules
    defineField({
      name: 'performanceAnalysisRules',
      type: 'object',
      title: 'Performance Analysis Generation Rules',
      group: 'buyerGuides',
      options: {
        collapsible: true,
        collapsed: false,
      },
      fields: [
        {
          name: 'enabled',
          type: 'boolean',
          title: 'Enable Auto-Generation',
          initialValue: true,
        },
        {
          name: 'promptTemplate',
          type: 'text',
          title: 'Prompt Template',
          description: 'Use {{variable}} syntax for context interpolation. Click "Open Editor" for field picker and live preview.',
          rows: 10,
          components: {
            input: PromptTemplateInput,
          },
        },
        {
          name: 'maxLength',
          type: 'number',
          title: 'Max Length (characters)',
          initialValue: 600,
        },
      ],
    }),

    // Ease of Fitment Rules
    defineField({
      name: 'easeOfFitmentRules',
      type: 'object',
      title: 'Ease of Fitment Analysis Rules',
      group: 'buyerGuides',
      options: {
        collapsible: true,
        collapsed: false,
      },
      fields: [
        {
          name: 'enabled',
          type: 'boolean',
          title: 'Enable Auto-Generation',
          initialValue: true,
        },
        {
          name: 'promptTemplate',
          type: 'text',
          title: 'Prompt Template',
          description: 'Use {{variable}} syntax for context interpolation. Click "Open Editor" for field picker and live preview.',
          rows: 10,
          components: {
            input: PromptTemplateInput,
          },
        },
        {
          name: 'maxLength',
          type: 'number',
          title: 'Max Length (characters)',
          initialValue: 500,
        },
      ],
    }),

    // Tire Availability Rules
    defineField({
      name: 'tireAvailabilityRules',
      type: 'object',
      title: 'Tire Availability Analysis Rules',
      group: 'buyerGuides',
      options: {
        collapsible: true,
        collapsed: false,
      },
      fields: [
        {
          name: 'enabled',
          type: 'boolean',
          title: 'Enable Auto-Generation',
          initialValue: true,
        },
        {
          name: 'promptTemplate',
          type: 'text',
          title: 'Prompt Template',
          description: 'Use {{variable}} syntax for context interpolation. Click "Open Editor" for field picker and live preview.',
          rows: 10,
          components: {
            input: PromptTemplateInput,
          },
        },
        {
          name: 'maxLength',
          type: 'number',
          title: 'Max Length (characters)',
          initialValue: 500,
        },
      ],
    }),
  ],
})
